import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import adminApi from '../../services/adminApi';

const DOMAINS = [
  { value: 'wms', label: 'WMS' },
  { value: 'iseries', label: 'iSeries' },
  { value: 'civil', label: 'Civil' },
  { value: 'stitching', label: 'Stitching' },
  { value: 'platform', label: 'Platform' },
  { value: 'skillcode', label: 'SkillCode' },
];

const emptyRow = () => ({ question: '', topic: '' });

// Parses seed JSON (object `{questions:[...]}` OR bare array). Returns cleaned
// rows; throws a string-friendly error on invalid JSON or zero valid rows.
function parseSeedJson(text) {
  let data;
  try {
    data = JSON.parse(text);
  } catch (err) {
    throw new Error(`Invalid JSON: ${err.message}`);
  }
  const list = Array.isArray(data)
    ? data
    : (data && Array.isArray(data.questions) ? data.questions : null);
  if (!list) {
    throw new Error('Expected an object with a "questions" array, or a bare array of {question, topic}.');
  }
  const cleaned = list
    .map((q) => ({
      question: String((q && q.question) || '').trim(),
      topic: String((q && q.topic) || '').trim(),
    }))
    .filter((q) => q.question.length > 0);
  if (cleaned.length === 0) {
    throw new Error('No valid rows found (every entry was missing a non-empty question).');
  }
  return cleaned;
}

export default function SpineSeeds() {
  const [domain, setDomain] = useState('wms');
  const [sets, setSets] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState([emptyRow()]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [topicCounts, setTopicCounts] = useState({});

  // Bulk-import UI state
  const [showImport, setShowImport] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [importMode, setImportMode] = useState('replace'); // 'replace' | 'append'

  const loadSets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminApi.listSpineSeedSets(domain);
      setSets(data.sets || []);
      const active = (data.sets || []).find((s) => s.isActive);
      if (active) {
        setSelectedId(active._id);
        setName(active.name);
        setDescription(active.description || '');
        setQuestions(active.questions && active.questions.length > 0
          ? active.questions.map((q) => ({ question: q.question, topic: q.topic || '' }))
          : [emptyRow()]);
      } else {
        setSelectedId(null);
        setName('');
        setDescription('');
        setQuestions([emptyRow()]);
      }
      setTopicCounts({});
    } catch (err) {
      setError(err.message || 'Failed to load seed sets');
    } finally {
      setLoading(false);
    }
  }, [domain]);

  useEffect(() => { loadSets(); }, [loadSets]);

  const selectSet = (set) => {
    setSelectedId(set._id);
    setName(set.name);
    setDescription(set.description || '');
    setQuestions(set.questions && set.questions.length > 0
      ? set.questions.map((q) => ({ question: q.question, topic: q.topic || '' }))
      : [emptyRow()]);
    setTopicCounts({});
    setError(null);
    setInfo(null);
  };

  const startNew = () => {
    setSelectedId(null);
    setName('');
    setDescription('');
    setQuestions([emptyRow()]);
    setTopicCounts({});
    setError(null);
    setInfo(null);
  };

  const updateRow = (i, field, value) => {
    setQuestions((rows) => rows.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
  };

  const addRow = () => setQuestions((rows) => [...rows, emptyRow()]);
  const removeRow = (i) => setQuestions((rows) => rows.filter((_, idx) => idx !== i));

  const testTopic = async (i) => {
    const topic = questions[i] && questions[i].topic;
    if (!topic) {
      setTopicCounts((c) => ({ ...c, [i]: { error: 'topic empty' } }));
      return;
    }
    setTopicCounts((c) => ({ ...c, [i]: { loading: true } }));
    try {
      const data = await adminApi.testSpineSeedTopic({ domain, topic });
      setTopicCounts((c) => ({ ...c, [i]: { count: data.count, hasGrounding: data.hasGrounding } }));
    } catch (err) {
      setTopicCounts((c) => ({ ...c, [i]: { error: err.message || 'wiki call failed' } }));
    }
  };

  // Drops the current empty placeholder row (if any) before appending so a
  // freshly-loaded blank set + an append doesn't leave a dead row at the top.
  const existingNonEmpty = () =>
    questions.filter((q) => (q && q.question || '').trim().length > 0);

  const applyImport = (parsed) => {
    if (importMode === 'append') {
      setQuestions([...existingNonEmpty(), ...parsed]);
    } else {
      setQuestions(parsed);
    }
    setTopicCounts({});
    setError(null);
    setInfo(`Imported ${parsed.length} row${parsed.length === 1 ? '' : 's'} (${importMode}). Click Save to persist.`);
  };

  const onImportPasted = () => {
    if (!pasteText.trim()) {
      setError('Paste some JSON first.');
      return;
    }
    try {
      const parsed = parseSeedJson(pasteText);
      applyImport(parsed);
      setPasteText('');
    } catch (err) {
      setError(err.message || 'Import failed');
      setInfo(null);
    }
  };

  const onImportFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = parseSeedJson(String(e.target.result || ''));
        applyImport(parsed);
      } catch (err) {
        setError(err.message || 'Import failed');
        setInfo(null);
      }
    };
    reader.onerror = () => {
      setError('Could not read file');
      setInfo(null);
    };
    reader.readAsText(file);
  };

  const onExportJson = () => {
    const cleaned = questions
      .map((q) => ({ question: (q.question || '').trim(), topic: (q.topic || '').trim() }))
      .filter((q) => q.question.length > 0);
    const payload = JSON.stringify({ questions: cleaned }, null, 2);
    const blob = new Blob([payload], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `seeds-${domain}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const save = async () => {
    if (!name.trim()) {
      setError('Set name is required');
      return;
    }
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const cleanedQuestions = questions
        .map((q) => ({ question: (q.question || '').trim(), topic: (q.topic || '').trim() }))
        .filter((q) => q.question.length > 0);

      let saved;
      if (selectedId) {
        saved = await adminApi.updateSpineSeedSet(selectedId, {
          name, description, questions: cleanedQuestions,
        });
      } else {
        saved = await adminApi.createSpineSeedSet({
          name, domain, description, questions: cleanedQuestions,
        });
        setSelectedId(saved.set && saved.set._id);
      }
      setInfo('Saved');
      loadSets();
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const activate = async () => {
    if (!selectedId) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      await adminApi.activateSpineSeedSet(selectedId);
      setInfo('Activated');
      loadSets();
    } catch (err) {
      setError(err.message || 'Activate failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
        <h1 style={{ marginBottom: 8 }}>Spine — Seed Questions</h1>
        <p style={{ color: '#666', marginBottom: 24 }}>
          Edit the questions distill uses to generate training pairs. The active
          set per vertical is read first; the legacy <code>seed-questions/&lt;domain&gt;.json</code>
          file is the fallback when no active set exists.
        </p>

        <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
          <label>Vertical:&nbsp;
            <select value={domain} onChange={(e) => setDomain(e.target.value)}>
              {DOMAINS.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </label>
          <button onClick={loadSets} disabled={loading}>Reload</button>
          <button onClick={startNew}>+ New set</button>
        </div>

        {error && <div style={{ background: '#fee', color: '#900', padding: 8, marginBottom: 12 }}>{error}</div>}
        {info && <div style={{ background: '#efe', color: '#060', padding: 8, marginBottom: 12 }}>{info}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 16 }}>
          <div style={{ borderRight: '1px solid #ddd', paddingRight: 12 }}>
            <h3>Sets for {domain}</h3>
            {loading && <div>Loading…</div>}
            {!loading && sets.length === 0 && <div style={{ color: '#888' }}>No sets yet.</div>}
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {sets.map((s) => (
                <li key={s._id} style={{ marginBottom: 8 }}>
                  <button
                    onClick={() => selectSet(s)}
                    style={{
                      width: '100%', textAlign: 'left',
                      background: selectedId === s._id ? '#eef' : 'transparent',
                      border: '1px solid #ddd', padding: 8, cursor: 'pointer',
                    }}
                  >
                    <div><strong>{s.name}</strong> {s.isActive && <span style={{ color: 'green' }}>(active)</span>}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>{s.questions ? s.questions.length : 0} questions</div>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div style={{ marginBottom: 12 }}>
              <label>Name:&nbsp;
                <input value={name} onChange={(e) => setName(e.target.value)} style={{ width: 360 }} />
              </label>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label>Description:&nbsp;
                <input value={description} onChange={(e) => setDescription(e.target.value)} style={{ width: 600 }} />
              </label>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Questions ({questions.length})</h3>
              <button type="button" onClick={() => setShowImport((v) => !v)}>
                {showImport ? 'Hide bulk import' : 'Bulk import'}
              </button>
              <button type="button" onClick={onExportJson} title={`Download seeds-${domain}.json`}>
                Download as JSON
              </button>
            </div>

            {showImport && (
              <div style={{ border: '1px solid #ddd', background: '#fafafa', padding: 12, marginBottom: 12 }}>
                <div style={{ marginBottom: 8, fontSize: 13 }}>
                  Import format: a JSON object <code>{'{"questions":[{"question":"...","topic":"..."}]}'}</code> or a bare array of the same shape.
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ marginRight: 12 }}>
                    <input
                      type="radio"
                      name="importMode"
                      value="replace"
                      checked={importMode === 'replace'}
                      onChange={() => setImportMode('replace')}
                    /> Replace
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="importMode"
                      value="append"
                      checked={importMode === 'append'}
                      onChange={() => setImportMode('append')}
                    /> Append
                  </label>
                </div>

                <div style={{ marginBottom: 8 }}>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Paste JSON:</label>
                  <textarea
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                    rows={6}
                    style={{ width: '100%', fontFamily: 'monospace', fontSize: 12 }}
                    placeholder='{"questions":[{"question":"...","topic":"..."}]}'
                  />
                  <div style={{ marginTop: 4 }}>
                    <button type="button" onClick={onImportPasted}>Import pasted</button>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Or upload a .json file:</label>
                  <input
                    type="file"
                    accept=".json,application/json"
                    onChange={(e) => {
                      const f = e.target.files && e.target.files[0];
                      onImportFile(f);
                      e.target.value = '';
                    }}
                  />
                </div>

                <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                  Import only updates the table state. Click <strong>Save</strong> below to persist.
                </div>
              </div>
            )}

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f6f6f6' }}>
                  <th style={{ width: 40 }}>#</th>
                  <th style={{ textAlign: 'left' }}>Question</th>
                  <th style={{ textAlign: 'left', width: 240 }}>Topic</th>
                  <th style={{ width: 160 }}>Wiki grounding</th>
                  <th style={{ width: 60 }} />
                </tr>
              </thead>
              <tbody>
                {questions.map((row, i) => {
                  const result = topicCounts[i];
                  return (
                    <tr key={i} style={{ borderTop: '1px solid #eee' }}>
                      <td style={{ textAlign: 'center', color: '#888' }}>{i + 1}</td>
                      <td>
                        <textarea
                          value={row.question}
                          onChange={(e) => updateRow(i, 'question', e.target.value)}
                          rows={2}
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td>
                        <input
                          value={row.topic}
                          onChange={(e) => updateRow(i, 'topic', e.target.value)}
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button type="button" onClick={() => testTopic(i)}>Test topic</button>
                        {result && result.loading && <div style={{ fontSize: 12 }}>…</div>}
                        {result && typeof result.count === 'number' && (
                          <div style={{ fontSize: 12, color: result.hasGrounding ? 'green' : '#a60' }}>
                            {result.count} entries {result.hasGrounding ? '✓' : '— empty grounding'}
                          </div>
                        )}
                        {result && result.error && (
                          <div style={{ fontSize: 12, color: '#900' }}>{result.error}</div>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button type="button" onClick={() => removeRow(i)}>×</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ marginTop: 8 }}>
              <button type="button" onClick={addRow}>+ Add question</button>
            </div>

            <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
              <button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              <button onClick={activate} disabled={!selectedId || saving}>Activate this set</button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
